import { Suspense, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/feature/Layout';
import FAQHero from './components/FAQHero';
import FAQCategories from './components/FAQCategories';
import FAQList from './components/FAQList';
import FAQCTA from './components/FAQCTA';

const FAQPage = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // 设置页面 title 和 description
    document.title = t('faq:meta.title');
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', t('faq:meta.description'));
    
    // 设置 canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = `${siteUrl}/faq`;
  }, [t]);

  // FAQ 数据
  const faqData = useMemo(() => [
    {
      category: 'general',
      question: t('faq:general.q1.question'),
      answer: t('faq:general.q1.answer')
    },
    {
      category: 'general',
      question: t('faq:general.q2.question'),
      answer: t('faq:general.q2.answer')
    },
    {
      category: 'general',
      question: t('faq:general.q3.question'),
      answer: t('faq:general.q3.answer')
    },
    {
      category: 'pdf',
      question: t('faq:pdf.q1.question'),
      answer: t('faq:pdf.q1.answer')
    },
    {
      category: 'pdf',
      question: t('faq:pdf.q2.question'),
      answer: t('faq:pdf.q2.answer')
    },
    {
      category: 'pdf',
      question: t('faq:pdf.q3.question'),
      answer: t('faq:pdf.q3.answer')
    },
    {
      category: 'pdf',
      question: t('faq:pdf.q4.question'),
      answer: t('faq:pdf.q4.answer')
    },
    {
      category: 'image',
      question: t('faq:image.q1.question'),
      answer: t('faq:image.q1.answer')
    },
    {
      category: 'image',
      question: t('faq:image.q2.question'),
      answer: t('faq:image.q2.answer')
    },
    {
      category: 'security',
      question: t('faq:security.q1.question'),
      answer: t('faq:security.q1.answer')
    },
    {
      category: 'security',
      question: t('faq:security.q2.question'),
      answer: t('faq:security.q2.answer')
    },
    {
      category: 'security',
      question: t('faq:security.q3.question'),
      answer: t('faq:security.q3.answer')
    },
    {
      category: 'account',
      question: t('faq:account.q1.question'),
      answer: t('faq:account.q1.answer')
    },
    {
      category: 'account',
      question: t('faq:account.q2.question'),
      answer: t('faq:account.q2.answer')
    },
    {
      category: 'account',
      question: t('faq:account.q3.question'),
      answer: t('faq:account.q3.answer')
    }
  ], [t]);

  // 过滤 FAQ
  const filteredFAQs = useMemo(() => {
    let filtered = faqData;

    // 按分类过滤
    if (activeCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === activeCategory);
    }

    // 按搜索关键词过滤
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(faq => 
        faq.question.toLowerCase().includes(query) || 
        faq.answer.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [faqData, activeCategory, searchQuery]);

  // FAQ Schema 结构化数据
  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema';
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('faq-schema');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [faqData]);

  return (
    <Layout>
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="text-gray-600">{t('common:loading')}</div></div>}>
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
          <FAQHero searchQuery={searchQuery} onSearchChange={setSearchQuery} />
          <FAQCategories activeCategory={activeCategory} onCategoryChange={setActiveCategory} />
          <FAQList faqs={filteredFAQs} searchQuery={searchQuery} />
          <FAQCTA />
        </div>
      </Suspense>
    </Layout>
  );
};

export default FAQPage;